import { TextProps, TextWordpressBlock, TextFontSize } from './text-types';
import { createEffect, For, Match, mergeProps, Switch } from 'solid-js';
import { ErrorCatcher } from '../../tools/error-catcher';
import { palette, resolveLegacyTextColor } from '../../style/theme';
import removeAmpersand from '../../tools/remove-ampersand';
import clsx from 'clsx';

const defaultProps = {
    fontSize: 'body-md' as TextFontSize,
    underlined: false,
};

const splitTextBlockToParagraphs = (textBlock: string) => {
    if (!textBlock.includes('\r\n\r')) {
        return [textBlock];
    }

    return textBlock.split('\r\n\r');
};

/* Only CVI body sizes (identity-mapped) and PDF variants live here. Legacy Atos
   sizes (normal, large, smaller, …) are remapped to CVI by LEGACY_FONTSIZE_MAP
   in resolvedFontSize() *before* this lookup, so they never reach it. */
const fontSizeClassMap: Partial<Record<TextFontSize, string>> = {
    // CVI DS body sizes — identity map to `.body-*` classes. The wrapper still
    // applies `.atos-text-base` for margins.
    'body-xs': 'body-xs',
    'body-sm': 'body-sm',
    'body-md': 'body-md',
    'body-lg': 'body-lg',
    'body-xl': 'body-xl',

    // PDF Builder styles — not remapped (LEGACY_FONTSIZE_MAP deliberately omits them).
    largestPDF: 'text-size-largest-pdf',
    largerPDF: 'text-size-larger-pdf',
    largePDF: 'text-size-large-pdf',
    normalPDF: 'text-size-normal-pdf',
    smallPDF: 'text-size-small-pdf',
    smallerPDF: 'text-size-smaller-pdf',
    tinyPDF: 'text-size-tiny-pdf',
};

/* Legacy Atos body size → CVI body size. Flatten mapping (size only), mirroring
   the heading LEGACY_VARIANT_MAP. Values match what the legacy `.text-size-*`
   classes already resolved to. PDF variants deliberately absent — pass through. */
const LEGACY_FONTSIZE_MAP: Partial<Record<TextFontSize, TextFontSize>> = {
    largest: 'body-xl',
    larger: 'body-lg',
    large: 'body-lg',
    normal: 'body-md',
    small: 'body-sm',
    smaller: 'body-xs',
    footerSmall: 'body-xs',
    footerHeading: 'body-sm',
};

// Get line limit inline styles (line-clamp requires inline due to dynamic value)
const getLineLimitStyles = (lineLimit: number | undefined): Record<string, string> => {
    if (!lineLimit) return {};
    return {
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': String(lineLimit),
    };
};

export const Text = (componentProps: TextProps) => {
    const props = mergeProps(defaultProps, componentProps);

    // eslint-disable-next-line
    let ref:HTMLParagraphElement | undefined = undefined;

    if (!props.children) {
        return null;
    }

    // This will make sure that any tags with the attachment datatype are opened in a new tab.
    // The main reason behind it is to make sure that files are opened without solidjs
    createEffect(() => {
        if (ref) {
            (ref as any).querySelectorAll('[data-type="attachment"]').forEach((atag: any) => {
                atag.target = '_blank';
            });
        }
    });

    // Only runs when `props.color` is set
    const getColor = () => {
        if (!props.color) return undefined;
        if (props.color === 'defer') return 'inherit';
        const themeColor = palette[props.color as keyof typeof palette];
        return themeColor ?? props.color;
    };

    const resolvedFontSize = () => LEGACY_FONTSIZE_MAP[props.fontSize] ?? props.fontSize;

    const textClasses = () => clsx(
        'atos-text-base',
        !props.color && 'text-cvi-fg-default',
        fontSizeClassMap[resolvedFontSize()],
        props.align && `text-${props.align}`,
        props.underlined && 'underline',
        props.fontStyle === 'italic' && 'italic',
        props.fontStyle === 'bold' && 'atos-text-bold',
        props.noBlockSpacing && 'no-block-spacing',
        props.pdfBuilderText && 'atos-text-pdf',
        props.isLastParagraphPdf && 'atos-text-pdf-last',
        !props.pdfBuilderText && 'atos-text',
        props.class,
    );

    // Combine color and line-limit into style object. Color only emitted when
    // an explicit `color` prop is set — the default is handled by the class.
    const getStyles = () => {
        const color = getColor();
        return {
            ...(color !== undefined ? { color } : {}),
            ...getLineLimitStyles(props.lineLimit),
        };
    };

    const Paragraph = (paragraphProps: { text: string }) => (
        <p
            ref={ref}
            class={textClasses()}
            innerHTML={removeAmpersand(paragraphProps.text)}
            style={getStyles()}
        />
    );

    return (
        <ErrorCatcher componentName='Text'>
            <Switch fallback={
                <Paragraph text={props.children} />
            }>
                <Match when={props.splitParagraph}>
                    <For each={splitTextBlockToParagraphs(props.children)}>{(text => (
                        <Paragraph text={text} />
                    ))}</For>
                </Match>
            </Switch>
        </ErrorCatcher>
    );
};

Text.parseProps = (atts: TextWordpressBlock) => {
    return {
        ...atts,
        color: resolveLegacyTextColor(atts.color),
    };
};
